import React, { useCallback, useEffect, useState } from 'react';

import { EmptyState, ErrorState, ListaSelecao, Modal, Pagination, Skeleton } from '../../../ui';

import { fetchSgaCores, Cor } from '../../../api/sga-cor/sga-corApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Escolha da cor do veículo (reescrito na at-59 sobre o `Modal` do kit).
 *
 * ## O que a reescrita mudou
 *
 *   1. **Visual do design system**, lista alcançável pelo teclado (era
 *      `<li onClick>`) e paginação em `<button>` (era `<div onClick>`).
 *   2. **Falha deixou de ser muda** — era toast + lista vazia, indistinguível de
 *      "nenhuma cor encontrada". Agora `ErrorState` com "Tentar novamente".
 *   3. 🐛 **A paginação passou a funcionar.** A chamada era
 *      `fetchSgaCores(1, 20)` — **página e limite fixos** —, enquanto o
 *      `totalPages` era calculado com limite 10. Resultado: "Ant."/"Próx."
 *      mudavam o estado, refaziam a requisição da **página 1** e a lista nunca
 *      saía do lugar; e o total de páginas exibido era o dobro do real.
 *   4. 🐛 **Os textos falavam de "cooperativas"** — o erro de carregamento e o
 *      estado vazio eram cópia do modal de cooperativas.
 *
 * ⚠️ **As props não mudaram** (`isOpen`/`onClose`/`onSelectCor`): o contrato é
 * lido pelo `FormularioVeiculo`.
 */

interface ModalCoresProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCor: (cor: Cor) => void;
}

type Estado = 'carregando' | 'ok' | 'erro';

const POR_PAGINA = 10;

const ModalCores: React.FC<ModalCoresProps> = ({ isOpen, onClose, onSelectCor }) => {
  const [cores, setCores] = useState<Cor[]>([]);
  const [total, setTotal] = useState(0);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    if (isOpen) setPagina(1);
  }, [isOpen]);

  const buscar = useCallback(async () => {
    setEstado('carregando');
    try {
      // 🐛 Era `fetchSgaCores(1, 20)`: a página não chegava à API e o limite não
      // batia com o usado no cálculo de páginas.
      const resposta = await fetchSgaCores(pagina, POR_PAGINA);

      // Resposta sem o array é resposta inesperada, não lista vazia.
      if (!resposta || !Array.isArray(resposta.items)) {
        setErro('A resposta do servidor não trouxe a lista de cores.');
        setEstado('erro');
        return;
      }

      setCores(resposta.items);
      setTotal(resposta.total ?? 0);
      setEstado('ok');
    } catch (falha) {
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar as cores.'));
      setEstado('erro');
    }
  }, [pagina]);

  useEffect(() => {
    if (isOpen) void buscar();
  }, [isOpen, buscar]);

  return (
    <Modal
      aberto={isOpen}
      titulo="Selecionar Cor"
      tamanho="sm"
      aoFechar={onClose}
      rodape={
        estado === 'ok' && cores.length > 0 ? (
          <Pagination
            pagina={pagina}
            totalItens={total}
            porPagina={POR_PAGINA}
            aoTrocar={setPagina}
          />
        ) : undefined
      }
    >
      {estado === 'carregando' && <Skeleton linhas={4} altura="tw-h-11" />}

      {estado === 'erro' && <ErrorState mensagem={erro} aoTentarNovamente={() => void buscar()} />}

      {estado === 'ok' && (
        <ListaSelecao
          itens={cores}
          chave={(c) => c.codigo_cor}
          rotulo={(c) => c.descricao}
          aoEscolher={(c) => {
            onSelectCor(c);
            onClose();
          }}
          rotuloAcessivel="Cores disponíveis"
          vazio={
            <EmptyState
              icone="sem-dados"
              titulo="Nenhuma cor encontrada"
              descricao="O espelho de cores do SGA parece vazio. Fale com o TI."
            />
          }
        />
      )}
    </Modal>
  );
};

export default ModalCores;
